/**
 * 同步与备份设置页
 * 对齐 features.md 2.9/9.5/11.1-11.6 + pages.md §十
 * 包含实际的 push/pull 同步操作（不只是 status）
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@hump/ui';
import { bffClient } from '../../lib/api/bff-client';
import { useAuthStore } from '../../stores/auth';

/** 格式化字节为 MB 字符串 */
function formatMB(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(2);
}

export function SyncPage() {
  const { user } = useAuthStore();
  const [syncing, setSyncing] = useState(false);
  const [syncAction, setSyncAction] = useState<'' | 'push' | 'pull' | 'status'>('');
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncScope, setSyncScope] = useState<Record<string, boolean> | null>(null);

  // 11.6 冲突裁决：push 返回的冲突列表
  const [conflicts, setConflicts] = useState<{ blockId: string; local?: any; remote?: any }[]>([]);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveResult, setResolveResult] = useState<string | null>(null);

  // 2.8 存储用量
  const [storageUsage, setStorageUsage] = useState<{ usage: number; quota: number } | null>(null);
  const [storageSupported, setStorageSupported] = useState(true);

  // 9.5 备份/恢复
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupResult, setBackupResult] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 加载存储用量
  async function refreshStorageUsage() {
    if (!('storage' in navigator) || !navigator.storage.estimate) {
      setStorageSupported(false);
      return;
    }
    try {
      const estimate = await navigator.storage.estimate();
      setStorageUsage({
        usage: estimate.usage ?? 0,
        quota: estimate.quota ?? 0,
      });
    } catch {
      setStorageSupported(false);
    }
  }

  useEffect(() => {
    refreshStorageUsage();
  }, []);

  async function doSync(action: 'status' | 'push' | 'pull') {
    if (!user) {
      setError('请先登录后再同步');
      return;
    }
    setSyncing(true);
    setSyncAction(action);
    setError(null);
    setSyncResult(null);
    setConflicts([]);
    setResolveResult(null);
    try {
      if (action === 'status') {
        const data = await bffClient.sync.status() as any;
        setSyncScope(data?.syncScope || null);
        setSyncResult(`待同步变更: ${data?.pendingChanges ?? 0} 项`);
      } else if (action === 'push') {
        // 推送本地 library/vocab 数据
        const { libraryRepository } = await import('../../lib/repository');
        const [lib] = await Promise.all([
          libraryRepository.getLibrary(),
          fetch('/src/lib/storage/factory').then(() => null).catch(() => null),
        ]);
        const changes = {
          library: lib,
          vocab: { upserted: [], deleted: [] },
        };
        const data = await bffClient.sync.push(changes) as any;
        setSyncResult(`推送完成: blocks=${data?.applied?.blocks ?? 0}, vocab=${data?.applied?.vocab ?? 0}`);
        // 11.6 冲突裁决：push 返回 conflicts 时显示裁决 UI
        const returnedConflicts = Array.isArray(data?.conflicts) ? data.conflicts : [];
        setConflicts(returnedConflicts);
        if (returnedConflicts.length > 0) {
          setSyncResult(`推送完成，但有 ${returnedConflicts.length} 个冲突需裁决`);
        }
        setLastSync(new Date().toLocaleString('zh-CN'));
      } else if (action === 'pull') {
        const data = await bffClient.sync.pull(lastSync || new Date(0).toISOString()) as any;
        const blockCount = data?.changes?.blocks?.length ?? 0;
        setSyncResult(`拉取完成: ${blockCount} 个 block 变更, hasMore=${data?.hasMore}`);
        setLastSync(new Date().toLocaleString('zh-CN'));
      }
    } catch (e) {
      setError((e as Error).message || '同步失败');
    }
    setSyncing(false);
    setSyncAction('');
  }

  // 11.6 冲突裁决：对单个冲突选择解决方案后提交
  async function handleResolveConflict(blockId: string, resolution: 'local' | 'remote' | 'skip') {
    setResolvingId(blockId);
    setError(null);
    try {
      await bffClient.sync.resolveConflicts([{ blockId, resolution }]);
      // 从列表移除已裁决项
      setConflicts((prev) => prev.filter((c) => c.blockId !== blockId));
      setResolveResult(`已裁决 block ${blockId.slice(0, 8)}…: ${resolution === 'local' ? '使用本地版本' : resolution === 'remote' ? '使用云端版本' : '跳过'}`);
    } catch (e) {
      setError((e as Error).message || '冲突裁决失败');
    }
    setResolvingId(null);
  }

  // 9.5 导出：读取 IndexedDB 中所有 library/vocab/knowledge 数据，合并为一个 JSON 下载
  async function handleExport() {
    setBackupBusy(true);
    setBackupResult(null);
    setError(null);
    try {
      const { libraryRepository, vocabRepository } = await import('../../lib/repository');
      const { knowledgeRepository } = await import('../../lib/repository/knowledge');
      const { getStorage } = await import('../../lib/storage/factory');

      const [library, vocab, knowledgeItems] = await Promise.all([
        libraryRepository.getLibrary(),
        vocabRepository.getAll().then((words) => ({ version: '1.0', words })),
        knowledgeRepository.getAll(),
      ]);

      // 导出每篇文档的 docIndex + blocks
      const docs = await Promise.all(
        library.documents.map(async (doc) => {
          const storage = getStorage();
          const index = await storage.readDocIndex(doc.id);
          const blockIds = await storage.listBlocks(doc.id);
          const blocks = await Promise.all(
            blockIds.map((id) => storage.readBlock(doc.id, id))
          );
          return { id: doc.id, index, blocks };
        })
      );

      const backup = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        library,
        vocab,
        knowledge: { version: '1.0', items: knowledgeItems },
        docs,
      };

      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hump-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setBackupResult(`已导出 ${library.documents.length} 篇文档、${vocab.words.length} 个生词、${knowledgeItems.length} 条知识点`);
      refreshStorageUsage();
    } catch (e) {
      setError((e as Error).message || '导出失败');
    }
    setBackupBusy(false);
  }

  // 9.5 导入：读取 JSON 文件，写回 IndexedDB
  function handleImportClick() {
    fileInputRef.current?.click();
  }

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重复导入同一文件
    if (!file) return;

    setBackupBusy(true);
    setBackupResult(null);
    setError(null);
    try {
      const text = await file.text();
      const backup = JSON.parse(text) as {
        library?: any;
        vocab?: any;
        knowledge?: { items?: any[] };
        docs?: { id: string; index: any; blocks: any[] }[];
      };

      const { getStorage } = await import('../../lib/storage/factory');
      const { knowledgeRepository } = await import('../../lib/repository/knowledge');
      const storage = getStorage();
      let docCount = 0;
      let blockCount = 0;

      // 写回 docs（index + blocks）
      if (Array.isArray(backup.docs)) {
        for (const doc of backup.docs) {
          await storage.writeDocIndex(doc.index);
          for (const block of doc.blocks) {
            await storage.writeBlock(doc.id, block);
            blockCount++;
          }
          docCount++;
        }
      }

      // 写回 library
      if (backup.library) {
        await storage.writeLibrary(backup.library);
      }

      // 写回 vocab
      if (backup.vocab) {
        await storage.writeVocab(backup.vocab);
      }

      // 写回 knowledge（追加）
      if (backup.knowledge?.items?.length) {
        for (const item of backup.knowledge.items) {
          await knowledgeRepository.update(item.id, item);
        }
      }

      setBackupResult(`已导入 ${docCount} 篇文档、${blockCount} 个 block`);
      refreshStorageUsage();
    } catch (e) {
      setError((e as Error).message || '导入失败：文件格式不正确');
    }
    setBackupBusy(false);
  }

  return (
    <div className="space-y-6 max-w-md">
      {/* 云同步 */}
      <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark">
        <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-3">云同步</h3>
        <p className="text-xs text-charcoal/50 dark:text-charcoal-light/50 mb-3">
          {user
            ? `已登录: ${user.email} (${user.tier})。${user.tier === 'free' ? 'Free 同步轻量数据（进度/生词/批注）。' : 'Pro 支持全量同步。'}`
            : '登录后可同步阅读进度、生词本、批注等到云端。'}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="ghost" size="sm" disabled={syncing} onClick={() => doSync('status')}>
            {syncing && syncAction === 'status' ? '查询中...' : '同步状态'}
          </Button>
          <Button variant="primary" size="sm" disabled={syncing || !user} onClick={() => doSync('push')}>
            {syncing && syncAction === 'push' ? '推送中...' : '推送数据'}
          </Button>
          <Button variant="secondary" size="sm" disabled={syncing || !user} onClick={() => doSync('pull')}>
            {syncing && syncAction === 'pull' ? '拉取中...' : '拉取数据'}
          </Button>
        </div>
        {syncScope && (
          <div className="mt-2 text-xs text-charcoal/50 dark:text-charcoal-light/50">
            同步范围: {Object.entries(syncScope).filter(([, v]) => v).map(([k]) => k).join(', ')}
          </div>
        )}
        {syncResult && <p className="text-xs text-oasis mt-2">{syncResult}</p>}
        {lastSync && <p className="text-xs text-charcoal/40 mt-1">上次同步: {lastSync}</p>}
        {error && <p className="text-xs text-error mt-2">{error}</p>}
        {conflicts.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs font-medium text-camel">检测到 {conflicts.length} 个冲突，请裁决：</p>
            {conflicts.map((c) => (
              <div key={c.blockId} className="p-2 rounded border border-border-warm dark:border-border-warm-dark bg-warm-white/50 dark:bg-warm-dark/50">
                <p className="text-xs text-charcoal dark:text-charcoal-light mb-1 font-mono break-all">blockId: {c.blockId}</p>
                <div className="flex gap-1.5 flex-wrap">
                  <Button variant="primary" size="sm" disabled={resolvingId === c.blockId} onClick={() => handleResolveConflict(c.blockId, 'local')}>
                    使用本地版本
                  </Button>
                  <Button variant="secondary" size="sm" disabled={resolvingId === c.blockId} onClick={() => handleResolveConflict(c.blockId, 'remote')}>
                    使用云端版本
                  </Button>
                  <Button variant="ghost" size="sm" disabled={resolvingId === c.blockId} onClick={() => handleResolveConflict(c.blockId, 'skip')}>
                    跳过
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {resolveResult && conflicts.length === 0 && <p className="text-xs text-oasis mt-2">{resolveResult}（全部冲突已处理）</p>}
      </div>

      {/* 本地备份 */}
      <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark">
        <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-3">本地备份</h3>
        <p className="text-xs text-charcoal/50 dark:text-charcoal-light/50 mb-3">
          导出全部数据（资料库、生词本、知识点、文档）为 JSON 文件，用于本地备份或迁移。
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" disabled={backupBusy} onClick={handleExport}>
            {backupBusy ? '处理中...' : '导出数据'}
          </Button>
          <Button variant="ghost" size="sm" disabled={backupBusy} onClick={handleImportClick}>
            导入恢复
          </Button>
          {/* 9.5 隐藏 file input，用于导入 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportFile}
          />
        </div>
        {backupResult && <p className="text-xs text-oasis mt-2">{backupResult}</p>}
      </div>

      {/* 存储用量 */}
      <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark">
        <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-3">存储用量</h3>
        <p className="text-xs text-charcoal/50 dark:text-charcoal-light/50 mb-2">
          数据存储在浏览器本地（IndexedDB），不占用服务器空间。
        </p>
        {storageSupported ? (
          storageUsage ? (
            <div className="space-y-1">
              <p className="text-sm text-charcoal dark:text-charcoal-light">
                已用 <span className="font-medium">{formatMB(storageUsage.usage)}</span> MB
                {storageUsage.quota > 0 && (
                  <> / 配额 {formatMB(storageUsage.quota)} MB</>
                )}
              </p>
              {storageUsage.quota > 0 && (
                <div className="w-full h-2 rounded-full bg-border-warm dark:bg-border-warm-dark overflow-hidden">
                  <div
                    className="h-full bg-camel transition-all"
                    style={{
                      width: `${Math.min(100, (storageUsage.usage / storageUsage.quota) * 100)}%`,
                    }}
                  />
                </div>
              )}
              {storageUsage.quota > 0 && (
                <p className="text-xs text-charcoal/40 dark:text-charcoal-light/40">
                  {((storageUsage.usage / storageUsage.quota) * 100).toFixed(1)}% 已使用
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-charcoal/40 dark:text-charcoal-light/40">查询中...</p>
          )
        ) : (
          <p className="text-xs text-charcoal/40 dark:text-charcoal-light/40">
            当前浏览器不支持存储用量查询。
          </p>
        )}
      </div>
    </div>
  );
}
